<script setup lang="ts">
import { ref, watch, onUnmounted } from 'vue'
import QRCode from 'qrcode'
import { useRoute } from 'vue-router'
import {
  collection,
  doc,
  onSnapshot,
  deleteDoc,
  query,
  where,
  Timestamp,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { useAuthStore } from '@/stores/auth'
import { useSyncStore } from '@/stores/sync'
import { useToastStore } from '@/stores/toast'
import { OVERHEAD_ROLE_OPTIONS } from '@/types'
import FosDialog from '@/components/ui/FosDialog.vue'
import FosButton from '@/components/ui/FosButton.vue'
import FosInput from '@/components/ui/FosInput.vue'
import FosSelect from '@/components/ui/FosSelect.vue'

interface IncidentInvite {
  invite_id: string
  email: string
  overhead_role: string | null
  status: string
  invited_by_name?: string
  created_at: Timestamp | null
  expires_at: number
}

const visible = defineModel<boolean>('visible', { default: false })
const route = useRoute()
const authStore = useAuthStore()
const syncStore = useSyncStore()
const toast = useToastStore()
const incidentId = route.params.id as string

const svgContent = ref('')
const joinUrl = ref('')
const loading = ref(false)

// ── Email invites ──────────────────────────────────────────────────────────
const inviteEmail = ref('')
const inviteRole = ref('')
const sendingInvite = ref(false)
const pendingInvites = ref<IncidentInvite[]>([])
let invitesUnsub: Unsubscribe | null = null

watch(visible, (val) => {
  if (val) {
    fetchToken()
    subscribeToInvites()
  } else {
    teardownInvites()
  }
})

onUnmounted(teardownInvites)

async function fetchToken() {
  if (!syncStore.isOnline) {
    toast.add('QR code generation requires a network connection.', 'warning', 4000)
    return
  }

  loading.value = true
  svgContent.value = ''

  try {
    const token = await authStore.currentUser?.getIdToken()
    const res = await fetch(`/api/incidents/${incidentId}/join-token`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
    const json = await res.json()
    if (res.status === 403) {
      toast.add("You don't have permission to perform this action.", 'error', 4000)
      visible.value = false
      return
    }
    if (!res.ok || json.error) {
      throw new Error(json.error?.message ?? 'Failed to generate token')
    }
    joinUrl.value = `${window.location.origin}${json.data.join_url}`
    svgContent.value = await QRCode.toString(joinUrl.value, { type: 'svg', margin: 2 })
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Failed to generate QR code'
    toast.add(message, 'error', 5000)
  } finally {
    loading.value = false
  }
}

async function downloadQR() {
  if (!joinUrl.value) return
  const dataUrl = await QRCode.toDataURL(joinUrl.value, { width: 512, margin: 2 })
  const a = document.createElement('a')
  a.href = dataUrl
  a.download = `incident-${incidentId}-checkin-qr.png`
  a.click()
}

function subscribeToInvites() {
  if (invitesUnsub) return
  const q = query(
    collection(db, 'incidents', incidentId, 'invites'),
    where('status', '==', 'pending'),
  )
  invitesUnsub = onSnapshot(
    q,
    (snap) => {
      const nowSec = Date.now() / 1000
      pendingInvites.value = snap.docs
        .map((d) => ({ invite_id: d.id, ...d.data() } as IncidentInvite))
        .filter((inv) => (inv.expires_at ?? 0) > nowSec)
        .sort((a, b) => (b.created_at?.toMillis() ?? 0) - (a.created_at?.toMillis() ?? 0))
    },
    () => {
      // permission-denied or offline — leave the list empty
      pendingInvites.value = []
    },
  )
}

function teardownInvites() {
  if (invitesUnsub) {
    invitesUnsub()
    invitesUnsub = null
  }
  pendingInvites.value = []
}

async function sendInvite() {
  const email = inviteEmail.value.trim().toLowerCase()
  if (!email) return
  if (!syncStore.isOnline) {
    toast.add('Sending an invite requires a network connection.', 'warning', 4000)
    return
  }
  sendingInvite.value = true
  try {
    const token = await authStore.currentUser?.getIdToken()
    const res = await fetch(`/api/incidents/${incidentId}/invite`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, overhead_role: inviteRole.value || null }),
    })
    const json = await res.json()
    if (res.status === 403) {
      toast.add("You don't have permission to perform this action.", 'error', 4000)
      return
    }
    if (!res.ok || json.error) {
      throw new Error(json.error?.message ?? json.detail ?? 'Failed to send invite')
    }
    inviteEmail.value = ''
    inviteRole.value = ''
    toast.add('Invite sent', 'success')
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Failed to send invite'
    toast.add(message, 'error', 5000)
  } finally {
    sendingInvite.value = false
  }
}

async function revokeInvite(inviteId: string) {
  try {
    await deleteDoc(doc(db, 'incidents', incidentId, 'invites', inviteId))
    toast.add('Invite revoked', 'success')
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Failed to revoke invite'
    toast.add(message, 'error', 4000)
  }
}
</script>

<template>
  <FosDialog :open="visible" title="Invite to Incident" max-width="max-w-md" @update:open="visible = $event">
    <div class="flex flex-col gap-6">
      <!-- QR code -->
      <div class="flex flex-col items-center gap-3">
        <div v-if="loading" class="w-44 h-44 flex items-center justify-center">
          <div class="w-8 h-8 border-2 border-outline/30 border-t-outline animate-spin" />
        </div>
        <div v-else-if="svgContent" v-html="svgContent" class="w-44 h-44 [&>svg]:w-full [&>svg]:h-full bg-white p-2" />
        <div v-else class="w-44 h-44 flex items-center justify-center text-outline text-sm text-center px-4 font-mono">
          QR code unavailable
        </div>
        <p class="text-[10px] text-outline font-mono uppercase tracking-widest">Scan to join this incident</p>
        <div class="flex flex-wrap gap-2 w-full">
          <FosButton variant="secondary" :disabled="loading" @click="fetchToken" class="flex-1">
            <span class="material-symbols-outlined text-[14px]">refresh</span>
            Regenerate
          </FosButton>
          <FosButton variant="secondary" :disabled="!svgContent || loading" @click="downloadQR" class="flex-1">
            <span class="material-symbols-outlined text-[14px]">download</span>
            Download
          </FosButton>
        </div>
      </div>

      <!-- Email invite -->
      <div class="flex flex-col gap-3 border-t border-border pt-5">
        <div>
          <p class="text-xs font-display uppercase tracking-widest text-on-surface-variant">Invite by email</p>
          <p class="text-[11px] text-outline mt-0.5">
            Sends a join link so someone can sign up and check in from their computer — no QR scan needed.
          </p>
        </div>
        <FosInput
          v-model="inviteEmail"
          type="email"
          placeholder="email@example.com"
          @keyup.enter="sendInvite"
        />
        <FosSelect
          v-model="inviteRole"
          :options="OVERHEAD_ROLE_OPTIONS"
          placeholder="Assign a role (optional)"
        />
        <FosButton
          variant="primary"
          :disabled="sendingInvite || !inviteEmail.trim()"
          @click="sendInvite"
        >
          <span v-if="sendingInvite" class="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
          <span v-else class="material-symbols-outlined text-[16px]">mail</span>
          Send Invite
        </FosButton>

        <!-- Pending invites -->
        <div v-if="pendingInvites.length" class="flex flex-col gap-1.5 mt-1">
          <p class="text-[10px] font-display uppercase tracking-widest text-outline">Pending invites</p>
          <div
            v-for="inv in pendingInvites"
            :key="inv.invite_id"
            class="flex items-center justify-between gap-2 border border-border rounded px-3 py-2 bg-surface-container-high"
          >
            <div class="min-w-0">
              <p class="text-sm text-on-surface truncate">{{ inv.email }}</p>
              <p v-if="inv.overhead_role" class="text-[11px] text-outline font-display uppercase tracking-wide">
                {{ inv.overhead_role }}
              </p>
            </div>
            <FosButton variant="tertiary" size="sm" @click="revokeInvite(inv.invite_id)">
              <span class="material-symbols-outlined text-[16px]">close</span>
            </FosButton>
          </div>
        </div>
      </div>
    </div>
  </FosDialog>
</template>
